import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
} from "react-native";
import React, { useState, useEffect } from "react";
import Colors from "../../../Resources/Color";
import GlobalStyles from "../../../Resources/Styles";
import Fonts from "../../../Resources/Fonts";

import { format } from "date-fns";

import { Ionicons } from "@expo/vector-icons";
import { observer, Observer } from "mobx-react";
import { useScreenDimensions, useDevice } from "../../_layout";
import IconEmptyView from "../../Components/IconEmptyView";
import { useTranslation } from "react-i18next";
import db from "../../Firestore/FirestoreConfig";
import { doc, updateDoc } from "firebase/firestore";
import { shouldSkipUserScopedForms } from "../../invitations/pendingClientDataAccess";

const ClientForms = observer(({ client }) => {
  const { screenSize } = useScreenDimensions();
  const { isMobile } = useDevice();
  const { t } = useTranslation();
  const SIDE_MENU_SIZE = 230;
  const shouldSkipFormsSync = shouldSkipUserScopedForms(client);

  useEffect(() => {
    (async () => {
      if (shouldSkipFormsSync) {
        client.syncStatusManager.setOperationStatus("fetchForms", null);
        client.syncStatusManager.setOperationStatus("listenerForms", null);
        return;
      }

      await client.syncStatusManager.safeOperation("fetchForms", () =>
        client.fetchFormsFromCloud()
      );

      await client.syncStatusManager.safeOperation("listenerForms", () =>
        client.firestoreListener.setupFormsListener()
      );

      if (!client.selectedForm && client?.forms?.length > 0) {
        const sortedForms = [...client.forms].sort(
          (a, b) => new Date(b.date) - new Date(a.date)
        );
        client.setSelectedForm(sortedForms[0]);
      }
    })();
  }, [client, shouldSkipFormsSync]);

  const SideView = observer(() => {
    const FormTypePicker = observer(() => {
      const TypeButton = observer(({ type, index }) => {
        const isSelected = client.formTypePicker === type;

        const [isHovered, setIsHovered] = useState(false);

        return (
          <TouchableOpacity
            key={index}
            onPress={() => {
              if (client.formTypePicker === type) {
                client.setProperty("formTypePicker", ""); // Deselect to show all
              } else {
                client.setProperty("formTypePicker", type); // Select type
              }
            }}
            style={{ flexShrink: 1 }}
            onMouseEnter={() => !isMobile && setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            <View style={{ alignItems: "center" }}>
              <Observer>
                {() => (
                  <View
                    style={{
                      padding: 8,
                      backgroundColor: isHovered
                        ? "rgba(63, 123, 141, 0.1)"
                        : isSelected
                        ? "rgba(63, 123, 141, 0.05)"
                        : "transparent",
                      borderRadius: 10,
                      maxWidth: "100%",
                    }}
                  >
                    <Text
                      numberOfLines={2}
                      style={{
                        color: isSelected
                          ? Colors.bubbleColor
                          : Colors.grayColor,
                        textAlign: "center",
                        opacity: isSelected ? 1 : 1,
                        maxWidth: "100%",
                      }}
                    >
                      {type === "Semanal"
                        ? t("weekly-string")
                        : t("endOfBlock-string")}
                    </Text>
                  </View>
                )}
              </Observer>
            </View>
          </TouchableOpacity>
        );
      });

      return (
        <View
          style={{
            flexDirection: "row",
            width: "100%",
            justifyContent: "space-around",
            marginBottom: screenSize === "small" ? 16 : 16,
          }}
        >
          <TypeButton type={"Semanal"} />
          <TypeButton type={"Fin de bloque"} />
        </View>
      );
    });

    const FormRow = observer(({ form }) => {
      const [isHovered, setIsHovered] = useState(false);
      const action = () => {
        client.setSelectedForm(form);
      };

      return (
        <View
          style={{
            width: SIDE_MENU_SIZE,
          }}
        >
          <TouchableOpacity
            onPress={action}
            style={[
              GlobalStyles.touchableRow,
              {
                shadowColor: Colors.bubbleColor,
                borderWidth:
                  form && form.isSelectedForAnswers === true ? 1 : 0.1,
                borderColor:
                  form && form.isSelectedForAnswers === true
                    ? Colors.blackColor
                    : isHovered
                    ? "rgba(0, 0, 0, 0.5)"
                    : Colors.borderColor,
              },
            ]}
            onMouseEnter={() => !isMobile && setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            <View style={styles.formRowContent}>
              <View style={styles.formRowTitleLine}>
                <Text
                  numberOfLines={1}
                  ellipsizeMode="tail"
                  style={[Fonts.medium14, styles.formRowTitle]}
                >
                  {form.name}
                </Text>
                {!form.getSeenByCoach && (
                  <View style={styles.formRowUnreadIndicator} />
                )}
              </View>

              <View style={[GlobalStyles.HStack, styles.formRowMetaLine]}>
                <View
                  style={[GlobalStyles.HStack, styles.formRowAnswersMetaItem]}
                >
                  <Ionicons
                    name="clipboard"
                    size={12}
                    color={Colors.bubbleColor}
                    style={GlobalStyles.rowIcon}
                  />
                  <Text
                    style={[
                      styles.formRowMetaText,
                      { fontSize: screenSize === "large" ? 12 : 11 },
                    ]}
                    numberOfLines={1}
                    ellipsizeMode="tail"
                  >
                    {form.numberOfQuestionsAnswered === 1
                      ? t("answersCountOne-string", {
                          count: form.numberOfQuestionsAnswered,
                        })
                      : t("answersCount-string", {
                          count: form.numberOfQuestionsAnswered,
                        })}
                  </Text>
                </View>
                <View style={[GlobalStyles.HStack, styles.formRowDateMetaItem]}>
                  <Ionicons
                    name="calendar"
                    size={12}
                    color={Colors.bubbleColor}
                    style={GlobalStyles.rowIcon}
                  />
                  <Text
                    style={[
                      styles.formRowMetaText,
                      { fontSize: screenSize === "large" ? 12 : 11 },
                    ]}
                    numberOfLines={1}
                  >
                    {format(form.date, "dd/MM/yy")}
                  </Text>
                </View>
              </View>
            </View>
          </TouchableOpacity>
        </View>
      );
    });
    return (
      <View
        style={{
          width: "100%",
          height: screenSize === "large" ? "100%" : "auto",
        }}
      >
        {client.forms.length > 0 && (
          <View
            style={{ width: screenSize === "large" ? SIDE_MENU_SIZE : "100%" }}
          >
            <FormTypePicker />
          </View>
        )}

        {screenSize != "large" ? (
          <ScrollView
            horizontal
            showsHorizontalScrollIndicator={false}
            contentContainerStyle={{
              flexDirection: "row",
              justifyContent: "flex-start",
              flex: 1,
              gap: screenSize === "small" ? 8 : 8,
            }}
          >
            {client.getFilteredClientForms().map((form, idx) => {
              return <FormRow form={form} key={idx} />;
            })}
          </ScrollView>
        ) : (
          <>
            <ScrollView
              style={{
                overflow: "scroll",
              }}
              contentContainerStyle={{ gap: 8 }}
              showsVerticalScrollIndicator={false}
              showsHorizontalScrollIndicator={false}
            >
              {client.getFilteredClientForms().map((form, idx) => (
                <FormRow form={form} key={idx} />
              ))}
            </ScrollView>
          </>
        )}
      </View>
    );
  });

  const FormDescriptionView = observer(({ form }) => {
    useEffect(() => {
      const markFormAsSeen = async () => {
        if (!form) return;

        const formDate = new Date(form.date);
        const cutoffDate = new Date("2025-12-04T00:00:00"); //Day we implemented seenByCoach forms feature

        // Check if form date is after December 4th, 2025 and hasn't been seen by coach
        if (formDate > cutoffDate && !form.getSeenByCoach) {
          if (shouldSkipFormsSync) return;

          // Update local property
          form.setProperty("seenByCoach", true);

          // Update Firestore
          try {
            const formRef = doc(db, "users", client.id, "forms", form.id);

            await updateDoc(formRef, {
              seen_by_coach: true,
            });
          } catch (error) {
            console.error("Error updating form seenByCoach:", error);
          }
        }
      };

      markFormAsSeen();
    }, [form?.id, form?.seenByCoach]);

    const SectionBlock = observer(({ section, idx }) => {
      const QuestionRow = observer(({ question, index }) => {
        return (
          question && (
            <View
              style={{
                width: "100%",
                borderLeftWidth: 5,
                borderLeftColor: Colors.bubbleColor,
                borderRadius: 5,
                paddingLeft: 4,
                marginTop: index !== 0 ? 20 : 0,
              }}
            >
              <View
                style={{
                  width: "100%",
                }}
              >
                <Text
                  style={{
                    color: Colors.grayColor,
                    fontSize: screenSize === "small" ? 12 : 14,
                    paddingLeft: 4,
                    marginBottom: 5,
                  }}
                >
                  {question.question}
                </Text>
                <Text
                  style={{
                    paddingLeft: screenSize === "small" ? 10 : 12,
                    fontSize: screenSize === "small" ? 12 : 14,
                  }}
                >
                  {question.answer}
                </Text>
              </View>
            </View>
          )
        );
      });
      return (
        <View>
          {idx !== 0 && <View style={styles.sectionSeparator} />}
          <View
            style={{
              flexDirection: "row",
              width: "100%",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Text
              style={{
                fontWeight: "600",
                fontSize: screenSize === "small" ? 12 : 14,
              }}
            >
              {section.name}
            </Text>
          </View>

          {section.questions &&
            section.questions
              .filter((q) => q.question !== "")
              .map((question, index) => (
                <QuestionRow question={question} key={index} />
              ))}
        </View>
      );
    });

    return (
      <View
        style={{
          width: "100%",
          backgroundColor: "white",
          borderRadius: 14,
          borderWidth: 0.1,
          borderColor: Colors.borderColor,
          paddingVertical: 16,
          paddingHorizontal: 12,
        }}
      >
        {form.type === "Fin de bloque" && (
          <View
            style={{ flexDirection: "row", marginTop: 0, marginBottom: 20 }}
          >
            <Text
              style={{
                // fontWeight: "600",
                fontSize: screenSize === "small" ? 12 : 14,
              }}
            >
              {t("nameOfBlockDots-string")}
            </Text>
            <Text
              style={{
                fontWeight: "600",
                fontSize: screenSize === "small" ? 12 : 14,
              }}
            >
              {form.blockName}
            </Text>
          </View>
        )}
        {form.sections &&
          form.sections.map((section, index) => (
            <SectionBlock section={section} idx={index} key={index} />
          ))}
        {/* <AddSectionButton /> */}
      </View>
    );
  });

  return (
    <View
      style={{
        flex: 1,
        paddingTop: screenSize === "small" ? 16 : 24,
      }}
    >
      {client.isOffline && (
        <View
          style={{
            flexDirection: "row",
            alignItems: "center",
            gap: 4,
            marginBottom: 16,
          }}
        >
          <Ionicons
            name="alert-circle-outline"
            size={20}
            color={Colors.grayColor}
          />
          <Text style={{ color: Colors.grayColor }}>
            {t("offlineFormsInformationMissing-string")}
          </Text>
        </View>
      )}
      <View
        style={{
          flexDirection: screenSize === "large" ? "row" : "column",
          flex: 1,
        }}
      >
        {client?.forms?.length > 0 ? (
          <>
            {screenSize === "large" ? (
              <View
                style={{
                  position: "absolute",
                  top: 0,
                  bottom: 0,
                  left: 0,
                  width: SIDE_MENU_SIZE + 8,
                  zIndex: 1,
                }}
              >
                <SideView />
              </View>
            ) : (
              <SideView />
            )}

            <Observer>
              {() => (
                <View
                  style={{
                    flex: 1,
                    flexGrow: 1,
                    paddingLeft: screenSize === "large" ? SIDE_MENU_SIZE : 0,
                    alignItems: "center",
                    marginLeft: screenSize === "large" ? 16 : 0,
                    marginTop:
                      screenSize === "small"
                        ? 8
                        : screenSize === "medium"
                        ? 16
                        : 0,
                  }}
                >
                  {client.selectedForm && (
                    <FormDescriptionView form={client.selectedForm} />
                  )}
                </View>
              )}
            </Observer>
          </>
        ) : (
          <IconEmptyView
            iconLibrary={"Ionicons"}
            mainText={t("withoutForms-string")}
            subText={
              shouldSkipFormsSync
                ? t("pendingClientFormsAfterAcceptance-string")
                : t("addFormsFromLibrary-string")
            }
            iconName={"reader-outline"}
            marginTop={0}
          />
        )}
      </View>
    </View>
  );
});

const styles = StyleSheet.create({
  searchBar: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 10,
    borderRadius: 14,
    height: 40,
  },
  textInputStyle: {
    flex: 1,
    paddingHorizontal: 10,
    borderRadius: 14,
    borderWidth: 0,
    fontWeight: "400",
    fontSize: 14,
  },
  tabText: {
    fontSize: 13,
    fontWeight: "500",
  },
  questionInputStyle: {
    width: "100%",
    borderColor: Colors.borderColor,
    backgroundColor: Colors.whiteColor,
    color: Colors.blackColor,
    borderWidth: 0.1,
    padding: 4,
    borderRadius: 10,
    outlineStyle: "none",
  },
  sectionInputStyle: {
    width: "100%",
    color: Colors.blackColor,
    outlineStyle: "none",
    paddingVertical: 4,
    fontWeight: "600",
  },
  sectionSeparator: {
    width: "95%",
    backgroundColor: "rgba(0,0,0,0.1)",
    height: 1,
    alignSelf: "center",
    marginVertical: 20,
  },
  formRowContent: {
    alignItems: "flex-start",
    justifyContent: "space-between",
    flex: 1,
    minWidth: 0,
  },
  formRowTitleLine: {
    flexDirection: "row",
    alignItems: "center",
    width: "100%",
    minWidth: 0,
    marginBottom: 6,
  },
  formRowTitle: {
    flex: 1,
    minWidth: 0,
    lineHeight: 17,
  },
  formRowUnreadIndicator: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: Colors.bubbleColor,
    marginLeft: 6,
    flexShrink: 0,
  },
  formRowMetaLine: {
    width: "100%",
    minWidth: 0,
    overflow: "hidden",
  },
  formRowAnswersMetaItem: {
    flexShrink: 1,
    minWidth: 0,
    marginRight: 12,
  },
  formRowDateMetaItem: {
    flexShrink: 0,
  },
  formRowMetaText: {
    color: "gray",
    flexShrink: 1,
    minWidth: 0,
    lineHeight: 15,
  },
});

export default ClientForms;
